
import { defineComponent } from 'vue';
import { useStore } from 'vuex';
import { computed } from '@vue/runtime-core';
import { Actions, formFieldsStoreV36, Getters, Mutations, userSettingsStore } from '@/store';

import Utils from '@/utils/QSUtils';
import ModalHeader from '@/modules/modals/components/ModalHeader.vue';
import ModalFooter from '@/modules/modals/components/ModalFooter.vue';
import ModalBody from '@/modules/modals/components/ModalBody.vue';
import ModalService from '@/modules/modals/services/modalService';
import SourceOrder from '@/modules/sort-order/SourceOrder.vue';
import FieldRules from '@/modules/field-rules/FieldRules.vue';
import PreferredCasing from '@/modules/casing/components/PreferredCasing.vue';
import FormsService from '@/services/3.6/FormsService';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import GuideManager from '@/manager/GuideManager';
import LogUsageManager from '@/manager/LogUsageManager';
import OriginalValuesManager from '@/manager/3.6/OriginalValuesManager';
import IComp from '@/models/Comp';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { newUpsertCompDataRequestByFieldId } from '@/store/3.6/models/IUpsertCompDataRequest';
import CompPrioritiesManager from '@/manager/3.6/CompPrioritiesManager';
import FormsManager from '@/manager/3.6/FormsManager';

export default defineComponent({
    components: { ModalHeader, ModalBody, ModalFooter, SourceOrder, PreferredCasing, FieldRules },
    props: {
        defaultTabIdx: {
            default: 0,
            required: false,
            type: Number,
        },
        hiddenTabIdx: {
            default: null,
            required: false,
            type: Array,
        },
        fieldId: {
            default: 0,
            required: true,
            type: Number,
        },
    },
    data() {
        return {
            modal: null,
            parentFieldId: 0,
            friendlyFieldName: '',
            fieldOriginalValue: '',
            fieldSourceInitial: '',
            sourceOrderCallback: {},
            fieldRulesCallback: [],
            fieldAwaitComplete: false,
            fieldRules: {},
            fieldSettings: {},
            activeComp: {} as IComp,
        };
    },
    setup() {
        const store = useStore();

        return {
            store,
        };
    },
    async beforeMount() {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();

        // Need to floor the fieldId because repeatable fields have a fieldId of a decimal value.  
        // The backend expects the fieldId to be an integer.
        this.parentFieldId = Math.floor(this.fieldId);
        this.fieldRules = await new FormsService(webEnvConfig).getDataFieldRules(this.parentFieldId);
        this.fieldSettings = await new FormsService(webEnvConfig).getDataFieldSettings(this.parentFieldId);

        const fieldData = await FormsManager.getFieldDataById(this.fieldId);
        this.friendlyFieldName = fieldData?.friendlyFieldName ?? '';

        const activeCompFieldData = computed(() => this.store.getters[Getters.CompAddressesV36.ActiveCompFieldDataByFieldId](this.fieldId));
        this.fieldSourceInitial = activeCompFieldData.value.sourceInitial;

        const activeComp = computed(() => this.store.getters[Getters.CompAddressesV36.ActiveComp]);
        this.activeComp = activeComp.value;
        const originalValue = OriginalValuesManager.getValueByAddressAndFieldId(this.activeComp.address, this.fieldId);
        this.fieldOriginalValue = originalValue ? originalValue.originalValue : activeCompFieldData.value.value;



        this.fieldAwaitComplete = true;
    },
    mounted() {
        const modalElement = this.$refs.dataPreferenceSettings;
        this.modal = ModalService.initialize(modalElement, this);
        ModalService.size(modalElement, 'xl');
        ModalService.open(this.modal);
    },
    computed: {
        SourceOrder: function () {
            const result = computed(() => userSettingsStore.DataSourceColumnOrder);
            return result.value;
        },
    },
    methods: {
        SaveDataPreferenceSettings: async function () {
            const sourceOrder: any = this.sourceOrderCallback;

            const fieldRules: any = this.fieldRulesCallback;

            const preferredCasing = document.querySelector('input[name="casing"]:checked') as HTMLInputElement;
            // @ts-expect-error: ...
            const originalPreferredCasingState = this.fieldSettings.preferredCasing;

            if (Utils.objectSize(sourceOrder) > 0) {
                await formFieldsStoreV36.saveDataPreferenceSourceOrder({
                    fieldId: this.parentFieldId,
                    dataPreferenceSourceOrder: sourceOrder,
                    formCode: theLocalStorageUtils.get(LocalStorageKeys.formCode),
                });

                // ReRun Data Preference Settings and Translations for all Tabs on the field
                await CompPrioritiesManager.runForAllCompsOnField(this.parentFieldId);
            }

            if (fieldRules.length > 0) {
                // Filter out all rules that are missing both textMatchCriteria and textEditValue
                const fieldRulesSave = fieldRules.filter((obj: { textMatchOption: number; textMatchCriteria: string; ruleStatus: string }) => (obj.textMatchOption === 3 && obj.textMatchCriteria === '' && obj.ruleStatus !== 'deleted') || (obj.textMatchCriteria !== '' && obj.ruleStatus !== 'deleted'));
                const fieldRulesLog = fieldRules.filter((obj: { textMatchCriteria: string; ruleStatus: string }) => obj.textMatchCriteria !== '');

                await formFieldsStoreV36.saveFieldRules({
                    fieldId: this.parentFieldId,
                    fieldRules: fieldRulesSave,
                });

                if (fieldRulesLog.length > 0) {
                    for (var i = 0; i < fieldRulesLog.length; i++) {
                        if (fieldRulesLog[i]['ruleId'] === 0 && fieldRulesLog[i]['ruleStatus'] === 'added') {
                            LogUsageManager.createdTranslationRule(fieldRulesLog[i], this.parentFieldId);
                        } else if (fieldRulesLog[i]['ruleId'] > 0 && fieldRulesLog[i]['ruleStatus'] === 'updated') {
                            LogUsageManager.editedTranslationRule(fieldRulesLog[i], this.parentFieldId);
                        } else if (fieldRulesLog[i]['ruleId'] > 0 && fieldRulesLog[i]['ruleStatus'] === 'deleted') {
                            LogUsageManager.deletedTranslationRule(fieldRulesLog[i], this.parentFieldId);
                        }
                    }
                }

                // Add UT sourceInitial if rule is applied to a transfer field that does not have a sourceInitial.  ie. isBlank condition
                if (this.fieldSourceInitial === '') {
                    const request = newUpsertCompDataRequestByFieldId(this.activeComp as IComp, this.parentFieldId);
                    request.sourceInitial = SourceDataInitials.UserOverride;
                    request.description = 'DataPreferenceSettings.SaveDataPreferenceSettings()';
                    this.store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldId, request);
                }

                // ReRun Rules for all Tabs
                await this.store.dispatch(Actions.CompAddressesV36.TransferRunFieldRowTransferValueRulesOnAllCompsByFieldId, this.parentFieldId);
            }

            if (originalPreferredCasingState !== preferredCasing.value) {
                // await this.store.dispatch(Actions.FormFields.PreferredCasing, {
                //     fieldId: this.fieldId,
                //     preferredCasing: preferredCasing.value,
                // });

                await formFieldsStoreV36.savePreferredCasing({
                    fieldId: this.parentFieldId,
                    preferredCasing: preferredCasing.value,
                });

                // ReRun Casing for all Tabs
                await this.store.dispatch(Actions.CompAddressesV36.TransferRunFieldRowTransferValueCasingOnAllCompsByFieldId, {
                    fieldId: this.parentFieldId,
                    preferredCasing: preferredCasing.value,
                });
                // this.store.dispatch(Actions.CompAddressesV36.TransferRunFieldRowTransferValueCasingOnAllUnVisitedCompsByFieldId, this.parentFieldId);
            }

            GuideManager.guideCompleted(5);
            ModalService.hide(this.modal);
        },
        isActiveTab(tabIdx: number) {
            return Number(this.defaultTabIdx) === tabIdx ? 'active' : '';
        },
        isActiveContent(tabIdx: number) {
            return Number(this.defaultTabIdx) === tabIdx ? 'active show' : '';
        },
        isHiddenTab(tabIdx: number) {
            return Utils.isValueInArray(this.hiddenTabIdx, tabIdx) ? true : false;
        },
        SourceOrderCallback(value: any) {
            this.sourceOrderCallback = value;
        },
        FieldRulesCallback(value: any) {
            this.fieldRulesCallback = value;
        },
    },
});
